import { useEffect, useMemo, useState } from "react";
import { ArrowPathIcon, CameraIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { IPartImage, IPartImageMpnAnalysis } from "../../models/Part";
import classNames from "../../utils/classNames";
import LoadingPulse from "../shared/LoadingPulse";

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  analysisData: IPartImageMpnAnalysis | null;
  isLoading: boolean;
  mpnImage?: IPartImage;
  currentMpn: string;
  partNumberAddedByAi?: boolean;
  canRunLookup?: boolean;
  onRunLookup?: () => void;
  onApplyMpn: (value: string) => void;
};

export default function StockMpnAnalysisModal({
  open,
  setOpen,
  analysisData,
  isLoading,
  mpnImage,
  currentMpn,
  partNumberAddedByAi,
  canRunLookup,
  onRunLookup,
  onApplyMpn,
}: Props) {
  const candidates = useMemo(() => {
    const parsed = (analysisData?.candidateMpns ?? "")
      .split(",")
      .map((x) => x.trim())
      .filter((x) => x.length > 0);

    return Array.from(new Set(parsed));
  }, [analysisData?.candidateMpns]);

  const [selectedCandidate, setSelectedCandidate] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedCandidate(analysisData?.bestMatchMpn ?? candidates[0] ?? "");
  }, [open, analysisData?.bestMatchMpn, candidates]);

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-6xl">
      <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">MPN Image Analysis</h3>
            <p className="text-xs text-slate-500">Detailed AI breakdown for part verification.</p>
          </div>
          <button
            type="button"
            onClick={onRunLookup}
            disabled={!canRunLookup || !onRunLookup || isLoading}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <ArrowPathIcon className={classNames("h-4 w-4", isLoading && "animate-spin")} />
            Run AI Lookup
          </button>
        </div>

        {isLoading && (
          <div className="py-8">
            <LoadingPulse />
          </div>
        )}

        {!isLoading && analysisData && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-7 rounded-lg border border-slate-200 bg-slate-50 p-3">
              {mpnImage ? (
                <img
                  src={mpnImage.url}
                  alt="MPN"
                  className="h-[26rem] w-full rounded-md border border-slate-200 bg-white object-contain"
                />
              ) : (
                <div className="flex h-[26rem] items-center justify-center rounded-md border border-dashed border-slate-300 bg-white text-slate-500">
                  <div className="flex flex-col items-center gap-2">
                    <CameraIcon className="h-8 w-8" />
                    <span className="text-sm">No MPN image found</span>
                  </div>
                </div>
              )}

              <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Analysis Summary</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{analysisData.details || "-"}</p>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-3">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Identified Part Number</p>
                <p className="mt-1 text-2xl font-bold text-primary-700">{analysisData.bestMatchMpn || "-"}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-slate-500">Found MPN</p>
                    <p className={analysisData.foundMpn ? "text-emerald-600 font-semibold" : "text-slate-700"}>
                      {analysisData.foundMpn ? "Yes" : "No"}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Confidence</p>
                    <p className="font-semibold text-slate-900">{analysisData.confidence}%</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500">Manufacturer</p>
                    <p className="font-medium text-slate-800">{analysisData.manufacturer || "Not detected"}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Candidate Matches</p>
                <div className="mt-2 space-y-2">
                  {candidates.length > 0 ? (
                    candidates.map((candidate) => (
                      <button
                        key={candidate}
                        type="button"
                        onClick={() => setSelectedCandidate(candidate)}
                        className={classNames(
                          "w-full rounded-md border px-3 py-2 text-left text-sm transition-colors",
                          selectedCandidate === candidate
                            ? "border-primary-400 bg-primary-50 text-primary-800"
                            : "border-slate-200 hover:bg-slate-50"
                        )}
                      >
                        {candidate}
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No candidates returned.</p>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Update Current MPN</p>
                <p className="mt-1 text-xs text-slate-500">
                  Current: <span className="font-semibold text-slate-800">{currentMpn || "-"}</span>
                </p>
                {partNumberAddedByAi && (
                  <p className="mt-1 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                    Current part number was added by AI
                  </p>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  Selected: <span className="font-semibold text-slate-800">{selectedCandidate || "-"}</span>
                </p>

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    disabled={!selectedCandidate}
                    onClick={() => {
                      if (!selectedCandidate) return;
                      onApplyMpn(selectedCandidate);
                      setOpen(false);
                    }}
                    className="rounded-md bg-primary-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    Use Selected MPN
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLoading && !analysisData && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-700">
              No analysis loaded yet for this image.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
